import type { DeviceSeriesRow } from '../types/missingReadings'

/**
 * Per-day delivery figures for the device Connectivity card.
 *
 * The API's expectedReadings is always a whole UTC day's worth, including for
 * today. Shown as-is, today reads "87 received, 4 missed, 95 expected" at
 * 22:30 and the numbers look like they fail to add up. Today's figure is
 * pro-rated to the time elapsed so far instead; past days pass through.
 */

const MINUTES_PER_DAY = 1440

/** YYYY-MM-DD for `now` in UTC, the same day key the API uses. */
export function utcDayKey(now: Date): string {
  return now.toISOString().slice(0, 10)
}

/**
 * Expected readings for `row.day` as of `now`: the full estimate for a past
 * day, a pro-rated share for today (UTC), and null where the API withheld it
 * (a reconfigured day).
 */
export function expectedSoFar(row: DeviceSeriesRow, now: Date): number | null {
  if (row.expectedReadings === null) return null
  const today = utcDayKey(now)
  if (row.day > today) return 0
  if (row.day < today) return row.expectedReadings
  const elapsedMinutes = now.getUTCHours() * 60 + now.getUTCMinutes()
  return Math.floor((row.expectedReadings * elapsedMinutes) / MINUTES_PER_DAY)
}

export type DeliveryTone = 'ok' | 'missed' | 'unknown'

export interface DeliveryBar {
  day: string
  /** 0..1, the share of the bar to fill. Unknown days draw full height. */
  fill: number
  tone: DeliveryTone
  /** True when `expected` is today's pro-rated figure rather than a full day's. */
  partial: boolean
  expected: number | null
  row: DeviceSeriesRow
  /** True for a day in the window with no row at all: nothing was heard. */
  empty?: boolean
}

/**
 * One bar per day, oldest first. The colour follows missedMessages, the exact
 * signal, so a day that lost messages but still looks full is red rather than
 * hidden.
 *
 * Height only varies on days that lost messages, where it shows how much still
 * arrived (received/expected, the estimate). A day with no missed messages is
 * a full bar: zero sequence gaps is exact, and a short green bar - from an
 * expected count that overshoots a device's real interval - would read as a
 * problem that the colour says is not there.
 */
export function deliveryBars(series: DeviceSeriesRow[], now: Date): DeliveryBar[] {
  const today = utcDayKey(now)
  return series.map((row) => {
    const expected = expectedSoFar(row, now)
    const tone: DeliveryTone = row.missedMessages > 0 ? 'missed' : expected === null ? 'unknown' : 'ok'
    const fill =
      row.missedMessages === 0 || expected === null || expected === 0
        ? 1
        : Math.min(row.receivedReadings / expected, 1)
    return { day: row.day, fill, tone, partial: row.day === today, expected, row }
  })
}

/**
 * deliveryBars over a fixed window: every UTC day ending today gets a bar, and
 * a day the series has no row for is an empty, grey bar. Strips stacked one
 * per device then line up day for day, which they would not if each device's
 * strip started on the first day it happened to report.
 */
export function deliveryBarsForWindow(series: DeviceSeriesRow[], days: number, now: Date): DeliveryBar[] {
  const byDay = new Map(deliveryBars(series, now).map((bar) => [bar.day, bar]))
  const today = utcDayKey(now)
  const todayStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  return Array.from({ length: days }, (_, i) => {
    const day = utcDayKey(new Date(todayStart - (days - 1 - i) * 86_400_000))
    return (
      byDay.get(day) ?? {
        day,
        fill: 0,
        tone: 'unknown' as const,
        partial: day === today,
        expected: null,
        row: { day, missedMessages: 0, receivedReadings: 0, expectedReadings: null, missingReadings: null, reconfigured: false },
        empty: true,
      }
    )
  })
}
